import type { FC } from 'react';
import React from 'react';
import {
  ReceiptBatchPaymentCard,
  ReceiptBatchSummaryCardDetailed,
} from '@pflegenavi/shared-web/receipt-batch';
import {
  useGetReceiptImagesWithTenant,
  useServiceProvider,
  useServiceProviderReceiptBatchPayment,
} from '@pflegenavi/service-provider-frontend/api';
import {
  ReceiptBatchPaymentState,
  ReceiptBatchState,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { useTranslation } from 'react-i18next';
import { ServiceProviderReceiptBatchDetails } from './form/ServiceProviderReceiptBatchDetails';
import type { BatchReceiptDetails } from './form/ServiceProviderReceiptBatchForm';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { ServiceProviderStripeOperatedBy } from '@pflegenavi/shared/api';

interface ReceiptBatchDetailsContainerProps {
  id: string | undefined;
  isLoading: boolean;
  receipts: BatchReceiptDetails[];
  autoGenerateReceipts?: boolean;
  generateReceiptBatchPreview?: () => Promise<Blob | undefined>;
  autoGenerateReceiptsPreview?: (entryId: string) => Promise<Blob | undefined>;
  totalAmount: number;
  receiptsCount: number;
  batchImageIds?: string[];
  receiptDate?: Date;
  nursingHomeName?: string;
  tenant?: string;
  state?: ReceiptBatchState;
  lastUpdateDate?: Date;
  useIndividualDates: boolean;
  minReceiptDate?: Date;
  maxReceiptDate?: Date;
}

export const ReceiptBatchDetailsContainer: FC<
  ReceiptBatchDetailsContainerProps
  // eslint-disable-next-line complexity
> = ({
  id,
  isLoading,
  receipts,
  autoGenerateReceipts,
  autoGenerateReceiptsPreview,
  tenant,
  batchImageIds,
  nursingHomeName,
  receiptsCount,
  totalAmount,
  receiptDate,
  state,
  lastUpdateDate,
  maxReceiptDate,
  minReceiptDate,
  useIndividualDates,
  generateReceiptBatchPreview,
}) => {
  const { t } = useTranslation();

  const useGetReceiptImages = useGetReceiptImagesWithTenant({
    tenant: tenant ?? '',
  });

  // @ts-expect-error // getTokenImmediate is intentionally hidden. It is necessary here to exctract the globalServiceProviderId
  const { getTokenImmediate } = useAuthentication();

  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );

  const { data: company } = useServiceProvider(
    {
      globalServiceProviderId: globalServiceProviderId ?? '',
    },
    {
      enabled: globalServiceProviderId !== undefined,
    }
  );

  const { data: paymentStatus } = useServiceProviderReceiptBatchPayment({
    receiptBatchId: id,
  });

  if (!tenant || !nursingHomeName || !company) {
    return null;
  }
  return (
    <ServiceProviderReceiptBatchDetails
      isLoading={isLoading}
      receipts={receipts}
      autoGenerateReceipts={autoGenerateReceipts}
      autoGenerateReceiptsPreview={autoGenerateReceiptsPreview}
      useReceiptImages={useGetReceiptImages}
      width={330}
      receiptDate={receiptDate}
      useIndividualDates={useIndividualDates}
      ReceiptBatchSummaryCard={
        <ReceiptBatchSummaryCardDetailed
          useReceiptImages={useGetReceiptImages}
          title={nursingHomeName ?? ''}
          receiptDate={new Date(receiptDate ?? Date.now())}
          useIndividualDates={useIndividualDates}
          minReceiptDate={minReceiptDate}
          maxReceiptDate={maxReceiptDate}
          totalAmount={totalAmount}
          receiptsCount={receiptsCount}
          receiptImageIds={batchImageIds}
          lastUpdateDate={lastUpdateDate}
          state={state ?? 'draft'}
          autoGenerateReceipts={autoGenerateReceipts}
          generateReceiptBatchPreview={generateReceiptBatchPreview}
        />
      }
      tooltipForInactiveResidentMessage={
        state !== ReceiptBatchState.Accepted &&
        state !== ReceiptBatchState.Declined
          ? t('receipts.batch.this-resident-was-archived-service-provider')
          : undefined
      }
      ReceiptBatchPaymentCard={
        company.stripeOperatedBy !==
          ServiceProviderStripeOperatedBy.Pflegenavi &&
        paymentStatus?.status === ReceiptBatchPaymentState.PAYMENT_INITIATED ? (
          <ReceiptBatchPaymentCard
            payment={{
              id: paymentStatus.payment.id,
              amountInCents: paymentStatus.payment.amount,
              completedAt: paymentStatus.payment.completedAt ?? undefined,
              initiationDate: paymentStatus.payment.created,
              status: paymentStatus.payment.status,
            }}
            isLoading={false}
            nursingHomeName={paymentStatus.payment.nursingHome.name}
          />
        ) : undefined
      }
    />
  );
};
